import { createBrowserRouter } from 'react-router-dom';
import MainLayoutProvider from './providers/MainLayoutProvider.js';
import MainLayout from './layouts/MainLayout.js';
import App from './App.js';
import SalesManagement from './pages/dashboard/SalesManagement.js';
import ProductionManagement from './pages/dashboard/ProductionManagement.js';
import Crm from './pages/dashboard/CRM.js';
import ComingSoon from './pages/ComingSoon.js';
import ProjectListView from './pages/production-management/ProjectListView.js';
import ProjectCardView from './pages/production-management/ProjectCardView.js';
import ProjectDetails from './pages/production-management/ProjectDetails.js';
import MachineMonitor from './pages/machines/MachineMonitor.js';
import MachineTraking from './pages/machines/MachineTraking.js';
import MachineControl from './pages/machines/MachineControl.js';
import Maintenence from './pages/machines/Maintenence.js';
import OrderListView from './pages/sales-management/OrderListView.js';
import OrderCardView from './pages/sales-management/OrderCardView.js';
import OrderReservations from './pages/sales-management/OrderReservations.js';
import OrderReservationBoard from './pages/sales-management/OrderReservationBoard.js';
import OrderDetails from './pages/sales-management/OrderDetails.js';
import ProtectedRoute from './ProtectedRoute.js';
import Error404 from './pages/error/Error403.js';
import L75Traking from './pages/machines/tracking/L75Traking.js';
import L70Traking from './pages/machines/tracking/L70Traking.js';
import L120Traking from './pages/machines/tracking/L120Traking.js';
import LDtbTraking from './pages/machines/tracking/LDtbTraking.js';
import Skip2250Tracking from './pages/machines/tracking/Skip2250Tracking.js';
import Dhc800Traking from './pages/machines/tracking/Dhc800Traking.js';
import Ach300Traking from './pages/machines/tracking/Ach300Traking.js';
import MachineAnalytics from './pages/machines/MachineAnalytics.js';
import AuthProvider from './providers/AuthProvider.js';
import { useAuthContext } from './providers/AuthProvider.js';
import Tv from './pages/tv/Tv.js';
import CreateNew from './pages/standard/CreateNew.js';
import MaterialCatalogue from './pages/standard/MaterialCatalogue.js';
import CreateStandardWizard from './pages/standard/CreateStandardWizard.js';
import StandardView from './pages/standard/StandardView.js';
import StandardConsumption from './pages/standard/StandardConsumption.js';
import StandardDrumLength from './pages/standard/StandardDrumLength.js';
import StandardAnalytics from './pages/standard/StandardAnalytics.js';
import StrandingCalculator from './pages/standard/StrandingCalculator.js';
import StandardOverview from './pages/standard/StandardOverview.js';
import CreateNewOrder from './pages/sales-management/CreateNewOrder.js';
import OrderProvider from './providers/OrderProvider.js';
import { WebSocketProvider } from './providers/WebSocketProvider.js';
import CreateNewProduct from './pages/production-management/CreateNewProduct.js';
import MachineDeepDive from './pages/machines/MachineDeepDive.js';
import ProjectMonitor from './pages/production-management/ProjectMonitor.js';
import FutureProducts from './pages/production-management/FutureProducts.js';
import ProjectAnalytics from './pages/production-management/ProjectAnalytics.js';
import MachineQueues from './pages/machines/MachineQueues.js';
import MachineQueue from './pages/machines/MachineQueue.js';
import Ath600Tracking from './pages/machines/tracking/Ath600Tracking.js';
import OrderSimulation from './pages/production-management/OrderSimulation.js';
import ProductionPlanner from './pages/production-management/ProductionPlanner.js';
import CreateLaunch from './pages/production-management/CreateLaunch.js';
import OrderMonitor from './pages/sales-management/OrderMonitor.js';
// Dedicated single-table pages. Reached from the "Full view" button on each
// table — deliberately NOT registered in sitemap.js, so they stay out of the nav.
import ProjectsFull from './pages/full-table/ProjectsFull.js';
import ProjectTokenStateFull from './pages/full-table/ProjectTokenStateFull.js';
import OrdersMonitorFull from './pages/full-table/OrdersMonitorFull.js';
import ProductionJobsFull from './pages/full-table/ProductionJobsFull.js';
import NextPhaseWizzard from './pages/production-management/NextPhaseWizzard.js';
import EditPhaseWizzard from './pages/production-management/EditPhaseWizzard.js';
import ReconcileWizzard from './pages/production-management/ReconcileWizzard.js';
import TokenChartsPage from './pages/production-management/TokenChartsPage.js';
import TransformWizzard from './pages/production-management/TransformWizzard.js';
import PackingStation from './pages/production-management/PackingStation.js';
import L120PrintingLabels from './pages/test-interfaces/L120PrintingLabels.js';
import CoilsPrintingStation from './pages/test-interfaces/CoilsPrintingStation.js';
import WarehouseReception from './pages/test-interfaces/WarehouseReception.js';
import MachineQueueEdit from './pages/machines/MachineQueueEdit.js';
import MachineHmiPanel from './pages/machines/MachineHmiPanel.js';
import ExecutionHistory from './pages/machines/ExecutionHistory.js';
import DrumLedger from './pages/machines/DrumLedger.js';
import FactorySync from './pages/production-management/FactorySync.js';
import Factory3D from './pages/factory-3d/Factory3DPage.js';
import OperatorPerformance from './pages/operators/OperatorPerformance.js';
import OperatorManagement from './pages/dashboard/OperatorManagement.js';
import OperatorMonitor from './pages/operators/OperatorMonitor.js';
import FactoryAlerts from './pages/machines/FactoryAlerts.js';
import AlertNotifications from './pages/machines/AlertNotifications.js';
import OperatorList from './pages/operators/OperatorList.js';
import OperatorDetail from './pages/operators/OperatorDetail.js';
import DrumList from './pages/drums/DrumList.js';
import RawMaterialList from './pages/material-management/RawMaterialList.js';
import UploadPackingList from './pages/material-management/UploadPackingList.js';
import WarehouseDashboard from './pages/warehouse/DashboardPage.js';
import WarehouseMap from './pages/warehouse/WarehouseMap.js';
import WarehouseSeed from './pages/warehouse/WarehouseSeed.js';
import WarehouseTasksPage from './pages/warehouse/WarehouseTasksPage.js';
import WarehouseTripsContainer from './pages/warehouse/TripsContainer.js';
import WarehouseKpiPage from './pages/warehouse/KpiPage.js';
import LoadTruckSimulator from './pages/warehouse/LoadTruckSimulator.js';
import ActivityPage from './pages/factory-events/ActivityPage';
import ReportsPage from './pages/factory-events/ReportsPage';
import DeletedOrders from './pages/sales-management/DeletedOrders';
import OrdersAnalytics from './pages/sales-management/OrdersAnalytics.js';

const DynamicIndexRoute = () => {
  const [state] = useAuthContext();

  // Determine which page to show based on the user's role
  if (state.user.role === 'admin' || state.user.role === 'production') {
    return <ProductionManagement />;
  } else if (state.user.role === 'sales') {
    return <SalesManagement />;
  }

  // Fallback if no role is set
  return <div>No role found. Please contact the administrator.</div>;
};

const routes = [
  {
    element: <App />,
    children: [
      {
        path: '/',
        element: (
          <ProtectedRoute
            element={
              <WebSocketProvider>
                <MainLayoutProvider>
                  <MainLayout />
                </MainLayoutProvider>
              </WebSocketProvider>
            }
          ></ProtectedRoute>
        ),
        children: [
          {
            index: true, // Dynamic index based on role
            element: <DynamicIndexRoute /> // This will determine the default page based on the role
          },
          {
            path: '/dashboard',
            children: [
              {
                path: 'sales-management',
                element: (
                  <ProtectedRoute
                    element={<SalesManagement />}
                  ></ProtectedRoute>
                )
              },
              {
                path: 'production-management',
                element: (
                  <ProtectedRoute
                    element={<ProductionManagement />}
                  ></ProtectedRoute>
                )
              },
              {
                path: 'crm',
                element: (
                  <ProtectedRoute
                    element={<Crm />}
                  ></ProtectedRoute>
                )
              },
              {
                path: 'warehouse-management',
                element: (
                  <ProtectedRoute
                    element={<WarehouseDashboard />}
                  ></ProtectedRoute>
                )
              },
              {
                path: 'operator-management',
                element: (
                  <ProtectedRoute
                    element={<OperatorManagement />}
                  ></ProtectedRoute>
                )
              }
            ]
          },
          {
            path: '/apps/',
            children: [
              // The factory's own history — the same two feeds the dashboard
              // cards show, but with a day, filters and pages behind them.
              {
                path: 'activity',
                element: <ProtectedRoute element={<ActivityPage />}></ProtectedRoute>
              },
              {
                path: 'reports',
                element: <ProtectedRoute element={<ReportsPage />}></ProtectedRoute>
              },
              {
                path: 'sales-management',
                children: [
                  {
                    path: 'deleted-orders',
                    element: <ProtectedRoute element={<DeletedOrders />}></ProtectedRoute>
                  },
                  {
                    path: 'orders-analytics',
                    element: (
                      <ProtectedRoute element={<OrdersAnalytics />}></ProtectedRoute>
                    )
                  },
                  {
                    path: 'create-new',
                    element: (
                      <ProtectedRoute
                        element={<CreateNewOrder />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'order-monitor',
                    element: (
                      <ProtectedRoute
                        element={
                          // <OrderProvider>
                          <OrderMonitor />
                          // </OrderProvider>
                        }
                      ></ProtectedRoute>
                    )
                  },
                  // Full-view pages for the two data-heavy tables on Order
                  // Monitor. Not in the nav — reached from their "Full view"
                  // button. See pages/full-table/routes.js.
                  {
                    path: 'full/orders-monitor',
                    element: (
                      <ProtectedRoute
                        element={<OrdersMonitorFull />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'full/production-jobs',
                    element: (
                      <ProtectedRoute
                        element={<ProductionJobsFull />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'order-list-view',
                    element: (
                      <ProtectedRoute
                        element={
                          // <OrderProvider>
                          <OrderListView />
                          // </OrderProvider>
                        }
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'order-card-view',
                    element: (
                      <ProtectedRoute
                        element={
                          // <OrderProvider>
                          <OrderCardView />
                          // </OrderProvider>
                        }
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'reservations',
                    element: (
                      <ProtectedRoute
                        element={<OrderReservations />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'reservations/:orderId',
                    element: (
                      <ProtectedRoute
                        element={<OrderReservationBoard />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'order-details/:id',
                    element: (
                      <ProtectedRoute
                        element={<OrderDetails />}
                      ></ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: 'crm',
                children: [
                  {
                    path: 'analytics'
                    // element: <Analytics />
                  },
                  {
                    path: 'deals'
                    // element: <ComingSoon />
                  },
                  {
                    path: 'deal-details'
                    // element: <DealDetails />
                  },
                  {
                    path: 'report-details'
                    // element: <ReportDetails />
                  },
                  {
                    path: 'leads'
                    // element: <Leads />
                  },
                  {
                    path: 'lead-details'
                    // element: <LeadDetails />
                  },
                  {
                    path: 'reports'
                    // element: <Reports />
                  },
                  {
                    path: 'add-contact'
                    // element: <AddContact />
                  }
                ]
              },
              {
                path: 'project-management',
                children: [
                  {
                    path: 'create-new',
                    element: (
                      <ProtectedRoute
                        element={<CreateNewProduct />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'create-launch',
                    element: (
                      <ProtectedRoute
                        element={<CreateLaunch />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'factory-sync',
                    element: (
                      <ProtectedRoute
                        element={<FactorySync />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'factory-3d',
                    element: (
                      <ProtectedRoute
                        element={<Factory3D />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'packing-station',
                    element: (
                      <ProtectedRoute
                        element={<PackingStation />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'production-planner',
                    element: (
                      <ProtectedRoute
                        element={<ProductionPlanner />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'order-simulation',
                    element: (
                      <ProtectedRoute
                        element={<OrderSimulation />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'plan-next-phase-wizard',
                    element: (
                      <ProtectedRoute
                        element={<NextPhaseWizzard />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'edit-phase-wizard',
                    element: (
                      <ProtectedRoute
                        element={<EditPhaseWizzard />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    // A route rather than the fullscreen modal it started as:
                    // the modal covered the navbar and dropped you out of the
                    // application. Everything it needs travels in the router's
                    // location state, so nothing is re-fetched.
                    path: 'token-charts',
                    element: (
                      <ProtectedRoute
                        element={<TokenChartsPage />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'reconcile-wizard',
                    element: (
                      <ProtectedRoute
                        element={<ReconcileWizzard />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'transform-wizard',
                    element: (
                      <ProtectedRoute
                        element={<TransformWizzard />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'project-monitor',
                    element: (
                      <ProtectedRoute
                        element={<ProjectMonitor />}
                      ></ProtectedRoute>
                    )
                  },
                  // Full-view pages for the two data-heavy tables on Project
                  // Monitor. Not in the nav — reached from their "Full view"
                  // button. See pages/full-table/routes.js.
                  {
                    path: 'full/projects',
                    element: (
                      <ProtectedRoute element={<ProjectsFull />}></ProtectedRoute>
                    )
                  },
                  {
                    path: 'full/token-state',
                    element: (
                      <ProtectedRoute
                        element={<ProjectTokenStateFull />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'future-products',
                    element: (
                      <ProtectedRoute
                        element={<FutureProducts />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'project-list-view',
                    element: (
                      <ProtectedRoute
                        element={<ProjectListView />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'project-card-view',
                    element: (
                      <ProtectedRoute
                        element={<ProjectCardView />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'project-analytics',
                    element: (
                      <ProtectedRoute
                        element={<ProjectAnalytics />}
                      ></ProtectedRoute>
                    )
                  },

                  {
                    path: 'todo-list'
                    // element: <ProjectTodoList />
                  },
                  {
                    path: 'project-details/:id',
                    element: (
                      <ProtectedRoute
                        element={<ProjectDetails />}
                      ></ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: 'machine-management',
                children: [
                  {
                    path: 'factory-alerts',
                    element: (
                      <ProtectedRoute
                        element={<FactoryAlerts />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'notifications',
                    element: (
                      <ProtectedRoute
                        element={<AlertNotifications />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'machine-monitor',
                    element: (
                      <ProtectedRoute
                        element={<MachineMonitor />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'machine-queues',
                    element: (
                      <ProtectedRoute
                        element={<MachineQueues />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'machine-queue/:machineId',
                    element: (
                      <ProtectedRoute
                        element={<MachineQueue />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'machine-queue/:machineId/edit',
                    element: (
                      <ProtectedRoute
                        element={<MachineQueueEdit />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'machine-hmi/:machineId',
                    element: (
                      <ProtectedRoute
                        element={<MachineHmiPanel />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'execution-history',
                    element: (
                      <ProtectedRoute
                        element={<ExecutionHistory />}
                      ></ProtectedRoute>
                    )
                  },
                  // A machine's finished drums as ONE run, across days — where a
                  // drum change the machine never registered is repaired. Not in
                  // the nav: reached from Execution History, because that is
                  // where you are standing when you notice.
                  {
                    path: 'drum-ledger/:machineId',
                    element: (
                      <ProtectedRoute element={<DrumLedger />}></ProtectedRoute>
                    )
                  },
                  {
                    path: 'machine-deep-dive',
                    element: (
                      <ProtectedRoute
                        element={<MachineDeepDive />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'machine-analytics',
                    element: (
                      <ProtectedRoute
                        element={<MachineAnalytics />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'machine-traking',
                    children: [
                      {
                        path: '',
                        element: (
                          <ProtectedRoute
                            element={<MachineTraking />}
                          ></ProtectedRoute>
                        )
                      },
                      {
                        path: 'L75',
                        element: (
                          <ProtectedRoute
                            element={<L75Traking />}
                          ></ProtectedRoute>
                        )
                      },
                      {
                        path: 'L70',
                        element: (
                          <ProtectedRoute
                            element={<L70Traking />}
                          ></ProtectedRoute>
                        )
                      },
                      ,
                      {
                        path: 'Ath600',
                        element: (
                          <ProtectedRoute
                            element={<Ath600Tracking />}
                          ></ProtectedRoute>
                        )
                      },
                      {
                        path: 'SKIP2250',
                        element: (
                          <ProtectedRoute
                            element={<Skip2250Tracking />}
                          ></ProtectedRoute>
                        )
                      },
                      {
                        path: 'L120',
                        element: (
                          <ProtectedRoute
                            element={
                              // <MachineDataProvider machineId="l120">
                              <L120Traking />
                              // </MachineDataProvider>
                            }
                          ></ProtectedRoute>
                        )
                      },
                      {
                        path: 'DTB1250',
                        element: (
                          <ProtectedRoute
                            element={
                              // <MachineDataProvider machineId="dtb1250">
                              <LDtbTraking />
                              // </MachineDataProvider>
                            }
                          ></ProtectedRoute>
                        )
                      },
                      {
                        path: 'ACH300',
                        element: (
                          <ProtectedRoute
                            element={
                              // <MachineDataProvider machineId="ach300">
                              <Ach300Traking />
                              // </MachineDataProvider>
                            }
                          ></ProtectedRoute>
                        )
                      },
                      {
                        path: 'DHC600',
                        element: (
                          <ProtectedRoute
                            element={
                              // <MachineDataProvider machineId="dhc600">
                              <Dhc800Traking />
                              // </MachineDataProvider>
                            }
                          ></ProtectedRoute>
                        )
                      }
                    ]
                  },
                  {
                    path: 'machine-control',
                    element: (
                      <ProtectedRoute
                        element={
                          // <MachineDataProvider machineId="machine-all">
                          <MachineControl />
                          // </MachineDataProvider>
                        }
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'maintenence',
                    element: (
                      <ProtectedRoute
                        element={<Maintenence />}
                      ></ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: 'operators',
                children: [
                  {
                    path: 'operator-list',
                    element: (
                      <ProtectedRoute
                        element={<OperatorList />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'operator-performance',
                    element: (
                      <ProtectedRoute
                        element={<OperatorPerformance />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'operator-monitor',
                    element: (
                      <ProtectedRoute
                        element={<OperatorMonitor />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: ':id',
                    element: (
                      <ProtectedRoute
                        element={<OperatorDetail />}
                      ></ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: 'drums',
                children: [
                  {
                    path: 'drum-list',
                    element: (
                      <ProtectedRoute
                        element={<DrumList />}
                      ></ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: 'warehouse-management',
                children: [
                  {
                    path: 'add-stock',
                    element: (
                      <ProtectedRoute
                        element={<WarehouseSeed />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'tasks',
                    element: (
                      <ProtectedRoute
                        element={<WarehouseTasksPage />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'map',
                    element: (
                      <ProtectedRoute
                        element={<WarehouseMap />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'trips',
                    element: (
                      <ProtectedRoute
                        element={<WarehouseTripsContainer />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'kpis',
                    element: (
                      <ProtectedRoute
                        element={<WarehouseKpiPage />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'load-truck-simulator',
                    element: (
                      <ProtectedRoute
                        element={<LoadTruckSimulator />}
                      ></ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: 'material-management',
                children: [
                  {
                    path: 'raw-materials',
                    element: (
                      <ProtectedRoute
                        element={<RawMaterialList />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'upload-packing-list',
                    element: (
                      <ProtectedRoute
                        element={<UploadPackingList />}
                      ></ProtectedRoute>
                    )
                  }
                ]
              },
              {
                path: 'standard',
                children: [
                  {
                    path: 'create-standard',
                    element: (
                      <ProtectedRoute
                        element={<CreateNew />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'create-standard-wizard',
                    element: (
                      <ProtectedRoute
                        element={<CreateStandardWizard />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'material-catalogue',
                    element: (
                      <ProtectedRoute
                        element={<MaterialCatalogue />}
                      ></ProtectedRoute>
                    )
                  },
                  {
                    path: 'view-standard',
                    element: <StandardView />
                  },
                  {
                    path: 'consumption-standard',
                    element: <StandardConsumption />
                  },
                  {
                    path: 'length-on-drum-standard',
                    element: <StandardDrumLength />
                  },
                  {
                    path: 'stranding-calculator',
                    element: <StrandingCalculator />
                  },
                  {
                    path: 'analytics',
                    element: <StandardAnalytics />
                  },
                  {
                    path: ':id',
                    element: <StandardOverview />
                  }
                ]
              },
              {
                path: 'events',
                children: [
                  {
                    path: 'create-an-event'
                    // element: <CreateAnEvent />
                  },
                  {
                    path: 'event-detail'
                    // element: <EventDetail />
                  }
                ]
              },
              {
                path: 'calendar',
                element: <ComingSoon title={'calendar'} />
              }
            ]
          },
          {
            path: '/test-interfaces/',
            children: [
              {
                path: 'l120-printing-labels',
                element: (
                  <ProtectedRoute
                    element={<L120PrintingLabels />}
                  ></ProtectedRoute>
                )
              },
              {
                path: 'coils-printing-station',
                element: (
                  <ProtectedRoute
                    element={<CoilsPrintingStation />}
                  ></ProtectedRoute>
                )
              },
              {
                path: 'warehouse-reception',
                element: (
                  <ProtectedRoute
                    element={<WarehouseReception />}
                  ></ProtectedRoute>
                )
              }
            ]
          },
          {
            path: '/pages/errors/',
            children: [
              {
                path: '404',
                element: <Error404 />
              }
            ]
          }
        ]
      },
      {
        path: '/tv',
        element: <Tv />
      }
    ]
  }
];

export const router = createBrowserRouter(routes);

export default routes;
